import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Space } from '@components/atoms/Space';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Faqs } from '@components/molecules/Faqs/Faqs';
import { Footer } from '@components/molecules/Footer';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { useGetPointsFaqs } from '@services/useGetPointsFaqs';
import { useData } from '@hooks/useData';
import { HeroSection } from '@components/molecules/HeroSection';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { Card } from '@components/atoms/Card';
import { useThemes } from '@hooks/useThemes';
import styles from './HowToUsePoints.module.css';
import { Modules, useModule } from '@hooks/useModule';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

export function HowToUsePoints() {
    const { navigate } = useCustomNavigate();
    const { useGrayscaleTheme } = useThemes();
    const { isModuleActive } = useModule();
    const { request: requestGetPointsFaqs } = useGetPointsFaqs();
    const { pointsFaqs, allSettings } = useData();
    const { t } = useTranslation();

    const translations = {
        overline: t('how-to-use-points-component.faqs.overline'),
        title: t('how-to-use-points-component.faqs.title'),
        termsAndConditionsLabelStart: t('how-to-use-points-component.terms-and-conditions.label.start'),
        termsAndConditionsLabelEnd: t('how-to-use-points-component.terms-and-conditions.label.end'),
        termsAndConditionsLink: t('how-to-use-points-component.terms-and-conditions.link'),
    };

    const [canAccessPage, setCanAccessPage] = useState<boolean | null>(null);

    // if gamification module is not active, user can't access this page
    useEffect(() => {
        if (allSettings !== null) {
            const pointsActive = isModuleActive(Modules.pointsGamification);
            pointsActive !== canAccessPage && setCanAccessPage(pointsActive);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings]);

    useEffect(() => {
        requestGetPointsFaqs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (canAccessPage) {
            requestGetPointsFaqs();
        } else if (canAccessPage === false) {
            // redirect to 404 page
            navigate(`/${SITE_URLS.pageNotFound}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAccessPage]);

    return (
        <>
            <StickyHeader />

            <HeroSection
                mode="wide"
                desktopImg={allSettings?.data?.content_points_page_initial_image}
                category={allSettings?.data?.content_points_page_initial_section_label || ''}
                title={allSettings?.data?.content_points_page_initial_title || ''}
                description={[
                    allSettings?.data?.content_points_page_initial_text || '',
                    <>
                        {translations.termsAndConditionsLabelStart}
                        <Anchor mode={AnchorMode.underline} href={SITE_URLS.policy.replace(':pageKey', 'terms-and-conditions')}>
                            {translations.termsAndConditionsLink}
                        </Anchor>
                        {translations.termsAndConditionsLabelEnd}
                    </>,
                ]}
                link={{
                    label: allSettings?.data?.content_points_page_initial_button_label || '',
                    url: SITE_URLS.pointsServices,
                }}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <Container>
                <div className="row">
                    <div className="col-lg-6 col-12">
                        <Typography classes="mb-2" color="muted" fontSize="12" styles={{ spacedText: true }}>
                            {allSettings?.data?.content_points_page_about_section_label}
                        </Typography>
                        <Typography
                            classes="mb-3"
                            tagVariant={TypographyTagVariants.h2}
                            appearanceLevel={TypographyAppearanceVariants.level2}>
                            {allSettings?.data?.content_points_page_about_title}
                        </Typography>
                    </div>
                    <div className="col-lg-6 col-12">
                        <Typography tagVariant={TypographyTagVariants.span}>
                            <div
                                className={`${
                                    styles['points-about-text'] + `  ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`
                                } text-muted pt-0 pt-lg-4 mt-0 mt-lg-2`}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(allSettings?.data?.content_points_page_about_text || ''),
                                }}></div>
                        </Typography>
                    </div>
                </div>
            </Container>

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <section className="bg-secondary">
                <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
                <Container>
                    <div className="mb-5 text-center">
                        <Typography classes="mb-3" color="muted" fontSize="12" styles={{ spacedText: true }}>
                            {allSettings?.data?.content_points_page_instructions_section_label}
                        </Typography>

                        <Typography
                            classes="mb-3 mb-md-0"
                            tagVariant={TypographyTagVariants.h2}
                            appearanceLevel={TypographyAppearanceVariants.level2}>
                            {allSettings?.data?.content_points_page_instructions_title}
                        </Typography>
                    </div>
                </Container>
                <Container>
                    <div className="row">
                        <div className="col-lg-4 col-12 mb-4 mb-lg-0">
                            <Card classes="h-100 p-4 border-0">
                                <div className="p-2">
                                    <Typography classes="mb-2 pb-1" fontSize="24">
                                        <i className="fa-sharp fa-solid fa-credit-card"></i>
                                    </Typography>
                                    <Typography classes="mb-3" fontWeight="900" fontSize="24">
                                        {allSettings?.data?.content_points_page_instructions_item_1_title}
                                    </Typography>
                                    <Typography color="muted" fontSize="18" tagVariant={TypographyTagVariants.span}>
                                        {allSettings?.data?.content_points_page_instructions_item_1_text}
                                    </Typography>
                                </div>
                            </Card>
                        </div>
                        <div className="col-lg-4 col-12 mb-4 mb-lg-0">
                            <Card classes="h-100 p-4 border-0">
                                <div className="p-2">
                                    <Typography classes="mb-2 pb-1" fontSize="24">
                                        <i className="fa-sharp fa-solid fa-gift"></i>
                                    </Typography>
                                    <Typography classes="mb-3" fontWeight="900" fontSize="24">
                                        {allSettings?.data?.content_points_page_instructions_item_2_title}
                                    </Typography>
                                    <Typography color="muted" fontSize="18" tagVariant={TypographyTagVariants.span}>
                                        {allSettings?.data?.content_points_page_instructions_item_2_text}
                                    </Typography>
                                </div>
                            </Card>
                        </div>
                        <div className="col-lg-4 col-12 mb-4 mb-lg-0">
                            <Card classes="h-100 p-4 border-0">
                                <div className="p-2">
                                    <Typography classes="mb-2 pb-1" fontSize="24">
                                        <i className="fa-sharp fa-solid fa-trophy"></i>
                                    </Typography>
                                    <Typography classes="mb-3" fontWeight="900" fontSize="24">
                                        {allSettings?.data?.content_points_page_instructions_item_3_title}
                                    </Typography>
                                    <Typography color="muted" fontSize="18" tagVariant={TypographyTagVariants.span}>
                                        {allSettings?.data?.content_points_page_instructions_item_3_text}
                                    </Typography>
                                </div>
                            </Card>
                        </div>
                    </div>
                </Container>
                <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />
            </section>

            {pointsFaqs?.data && (
                <>
                    <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

                    <Container>
                        <div className="mb-5 text-center">
                            <Typography classes="mb-3" color="muted" fontSize="12" styles={{ spacedText: true }}>
                                {translations.overline}
                            </Typography>

                            <Typography
                                classes="mb-3 mb-md-0"
                                tagVariant={TypographyTagVariants.h2}
                                appearanceLevel={TypographyAppearanceVariants.level2}>
                                {translations.title}
                            </Typography>
                        </div>
                    </Container>

                    <Container customSize="custom-large">
                        <Faqs
                            items={pointsFaqs.data.map((item) => {
                                return {
                                    question: item.question || '',
                                    answer: item.answer || '',
                                };
                            })}
                        />
                    </Container>
                </>
            )}

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
}
